import { useEffect, useState } from "react";
import API from "@/lib/api";
import { Bell, Loader2, Coins, Gift, Megaphone, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ICONS = {
  points_earned: { Icon: Coins, bg: "bg-emerald-50", clr: "text-emerald-600" },
  points_redeemed: { Icon: Gift, bg: "bg-rose-50", clr: "text-rose-600" },
  redemption: { Icon: Gift, bg: "bg-sky-50", clr: "text-sky-600" },
  announcement: { Icon: Megaphone, bg: "bg-amber-50", clr: "text-amber-600" },
};

const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get("/notifications");
        setItems(data || []);
        // Point 11: mark notifications as read the moment the page opens, then
        // tell the layout to refresh the red dot.
        try {
          await API.post("/notifications/read");
          window.dispatchEvent(new Event("notifications-updated"));
        } catch { /* ignore */ }
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="space-y-4" data-testid="notifications-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="notif-back-btn">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Notifications</h2>
          <p className="text-xs text-gray-500">{items.length} recent activity</p>
        </div>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#008A99]" /></div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center" data-testid="notif-empty">
          <div className="w-16 h-16 mx-auto rounded-full bg-gray-50 flex items-center justify-center mb-3">
            <Bell className="w-7 h-7 text-gray-300" />
          </div>
          <p className="text-sm text-gray-500 font-medium">No notifications yet</p>
          <p className="text-xs text-gray-400 mt-1">Earn points and read announcements to see updates here.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="notif-list">
          {items.map((n, i) => {
            const cfg = ICONS[n.type] || ICONS.announcement;
            const Icn = cfg.Icon;
            return (
              <div key={i} className="bg-white rounded-xl border p-3 flex items-start gap-3" data-testid={`notif-item-${i}`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
                  <Icn className={`w-5 h-5 ${cfg.clr}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{n.title}</p>
                  {n.subtitle && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.subtitle}</p>}
                  {n.created_at && <p className="text-[10px] text-gray-400 mt-1">{formatTime(n.created_at)}</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
